import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  planLightyearImport,
  type ImportPlan,
  type LightyearParseResult,
  type LightyearTransaction,
} from '@budget/core';
import { supabase } from '../../lib/supabase.js';
import { decimalInsert } from '../../types/db.js';
import type { TablesInsert } from '../../types/database.js';
import { queryKeys } from '../../lib/query-client.js';

/** PostgREST puts the whole `in` list in the URL, so ask in bites. */
const LOOKUP_CHUNK = 400;
/** Well under any statement limit, and small enough to fail cheaply. */
const WRITE_CHUNK = 500;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let start = 0; start < items.length; start += size) {
    out.push(items.slice(start, start + size));
  }
  return out;
}

export interface ImportAccount {
  id: string;
  provider: string;
  name: string;
}

/**
 * Accounts an import can target.
 *
 * Only ledger-sourced ones: a snapshot account (IBKR) is read wholesale from
 * `position_snapshots`, so transactions written against it would be invisible
 * at best and double-counted the moment its source changed.
 */
export function useImportAccounts({ enabled }: { enabled: boolean }) {
  return useQuery({
    queryKey: queryKeys.importAccounts,
    enabled,
    queryFn: async (): Promise<ImportAccount[]> => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, provider, name')
        .eq('is_active', true)
        .eq('position_source', 'ledger')
        .eq('type', 'broker')
        .order('name');

      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface LedgerLookup {
  /** External ids from the file that the account already carries. */
  existingExternalIds: Set<string>;
  /** Instrument id for every ISIN in the file that already has one. */
  instrumentIdByIsin: Map<string, string>;
}

/**
 * Ask only about what the file actually contains.
 *
 * Fetching the whole ledger instead would quietly hit PostgREST's row cap on
 * a long history and under-report what is already stored — the preview would
 * then promise to add rows that an upsert merely overwrites.
 */
export function useLedgerLookup({
  accountId,
  parsed,
  fileKey,
}: {
  accountId: string | null;
  parsed: LightyearParseResult | null;
  fileKey: string | null;
}) {
  return useQuery({
    queryKey: [...queryKeys.importLookup, accountId, fileKey],
    enabled: accountId !== null && parsed !== null,
    staleTime: 0,
    queryFn: async (): Promise<LedgerLookup> => {
      const transactions = parsed!.transactions;
      const externalIds = [...new Set(transactions.map((t) => t.externalId))];
      const isins = [...new Set(transactions.flatMap((t) => (t.isin === null ? [] : [t.isin])))];

      const existingExternalIds = new Set<string>();
      for (const batch of chunk(externalIds, LOOKUP_CHUNK)) {
        const { data, error } = await supabase
          .from('investment_transactions')
          .select('external_id')
          .eq('account_id', accountId!)
          .in('external_id', batch);

        if (error) throw error;
        for (const row of data ?? []) {
          if (row.external_id !== null) existingExternalIds.add(row.external_id);
        }
      }

      const instrumentIdByIsin = new Map<string, string>();
      for (const batch of chunk(isins, LOOKUP_CHUNK)) {
        const { data, error } = await supabase
          .from('instruments')
          .select('id, isin')
          .in('isin', batch);

        if (error) throw error;
        for (const row of data ?? []) {
          if (row.isin !== null) instrumentIdByIsin.set(row.isin, row.id);
        }
      }

      return { existingExternalIds, instrumentIdByIsin };
    },
  });
}

export function buildPlan(parsed: LightyearParseResult, lookup: LedgerLookup): ImportPlan {
  return planLightyearImport({
    parsed,
    existingExternalIds: lookup.existingExternalIds,
    knownIsins: new Set(lookup.instrumentIdByIsin.keys()),
  });
}

export interface ImportOutcome {
  instrumentsCreated: number;
  transactionsWritten: number;
}

/**
 * Write the plan.
 *
 * Instruments first, because the transactions reference them. Both writes go
 * through the user's own session, so RLS applies exactly as it does to every
 * other read — there is no service-role path into the browser.
 */
export function useRunImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      accountId,
      plan,
      lookup,
    }: {
      accountId: string;
      plan: ImportPlan;
      lookup: LedgerLookup;
    }): Promise<ImportOutcome> => {
      const instrumentIdByIsin = new Map(lookup.instrumentIdByIsin);

      if (plan.newInstruments.length > 0) {
        const { data, error } = await supabase
          .from('instruments')
          .insert(
            plan.newInstruments.map((instrument) => ({
              isin: instrument.isin,
              ticker: instrument.ticker,
              currency: instrument.currency,
              asset_class: instrument.assetClass,
              // Lightyear's export names no venue. Left null, which the
              // unique index treats as a value, so re-importing does not
              // create a second row.
              exchange: null,
              // The export carries no instrument name, and repeating the
              // ticker would render as "2B76 2B76". Left empty for the UI to
              // fall back on the ticker alone.
              name: '',
            })),
          )
          .select('id, isin');

        if (error) throw error;
        for (const row of data ?? []) {
          if (row.isin !== null) instrumentIdByIsin.set(row.isin, row.id);
        }
      }

      const rows = plan.toInsert.map((transaction) =>
        toLedgerRow(transaction, accountId, plan.parserVersion, instrumentIdByIsin),
      );

      for (const batch of chunk(rows, WRITE_CHUNK)) {
        const { error } = await supabase
          .from('investment_transactions')
          .upsert(decimalInsert<TablesInsert<'investment_transactions'>>(batch), {
            onConflict: 'account_id,external_id',
          });

        if (error) throw error;
      }

      return {
        instrumentsCreated: plan.newInstruments.length,
        transactionsWritten: rows.length,
      };
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.portfolio }),
        queryClient.invalidateQueries({ queryKey: queryKeys.importLookup }),
      ]);
    },
  });
}

function toLedgerRow(
  transaction: LightyearTransaction,
  accountId: string,
  parserVersion: number,
  instrumentIdByIsin: ReadonlyMap<string, string>,
): Record<string, unknown> {
  return {
    account_id: accountId,
    // Null for the cash-only rows: a conversion fee belongs to no holding.
    instrument_id:
      transaction.isin === null ? null : (instrumentIdByIsin.get(transaction.isin) ?? null),
    type: transaction.type,
    quantity: transaction.quantity,
    price: transaction.price,
    amount: transaction.amount,
    currency: transaction.currency,
    trade_date: transaction.tradeDate,
    external_id: transaction.externalId,
    // Which parser read the row, so a format change stays traceable.
    source: `lightyear:v${parserVersion}`,
  };
}
